import React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Paper, Typography, Box, Avatar, useTheme, Tooltip } from '@mui/material'; // Import Tooltip
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

const getInitials = (name = '') => name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

const scoreToColor = (score) => {
  const numericScore = Number(score);
  if (isNaN(numericScore)) return '#808080';
  return numericScore <= 0.5 ? "#D0312D" : numericScore <= 0.75 ? "#FC6A03" : "#028A0F";
};

const formatPhoneNumber = (str) => {
    if (!str) return 'N/A';
    return /^\+1\d{10}$/.test(str) ? str.replace(/^(\+1)(\d{3})(\d{3})(\d{4})$/, "$1 ($2) $3-$4") : str;
}

const ScoreBar = ({ label, score }) => {
    const theme = useTheme();
    const color = scoreToColor(score);
    const percentage = Math.round(score * 100);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Typography variant="caption" sx={{ width: '70px', flexShrink: 0 }}>{label}</Typography>
            <Box sx={{ flexGrow: 1, height: '8px', bgcolor: 'action.hover', borderRadius: '4px', overflow: 'hidden' }}>
                <Box sx={{ width: `${percentage}%`, height: '100%', bgcolor: color, transition: 'width 0.3s ease-in-out' }} />
            </Box>
            <Typography variant="body2" sx={{ width: '40px', textAlign: 'right', fontWeight: 500, color }}>
                {percentage}%
            </Typography>
        </Box>
    );
};

const CandidateTooltipContent = ({ candidate }) => (
    <Box sx={{ p: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Contact Information</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <EmailIcon fontSize="small" sx={{ mr: 1, color: 'grey.400' }} />
            <Typography variant="body2">{candidate.candidate.email || 'No Email'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'grey.400' }} />
            <Typography variant="body2">{formatPhoneNumber(candidate.candidate.phone)}</Typography>
        </Box>
    </Box>
);


export default function CandidateCard({ candidate, index }) {
  const skillScore = candidate.scorecardResults.find(s => s.scorecardName === "skills")?.score || 0;
  const behaviorScore = candidate.scorecardResults.find(s => s.scorecardName === "behaviors")?.score || 0;

  return (
    <Draggable draggableId={candidate.id} index={index}>
      {(provided, snapshot) => (
        <Tooltip 
            title={<CandidateTooltipContent candidate={candidate} />} 
            placement="right" 
            arrow
            PopperProps={{
                sx: {
                    '& .MuiTooltip-tooltip': {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        boxShadow: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                    },
                    '& .MuiTooltip-arrow': {
                        color: 'background.paper',
                    },
                }
            }}
        >
            <Paper
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              elevation={snapshot.isDragging ? 4 : 1}
              sx={{
                p: 2,
                mb: 1.5,
                userSelect: 'none',
                cursor: 'grab',
                borderLeft: `4px solid`,
                borderColor: snapshot.isDragging ? 'primary.main' : 'transparent',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                '&:hover': { boxShadow: 3 }
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'secondary.light', color: 'text.secondary', width: 32, height: 32, fontSize: '0.875rem', mr: 1.5 }}>
                  {getInitials(candidate.candidate.name)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" component="div" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                    {candidate.candidate.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Applied: {new Date(candidate.createdAt * 1000).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
              
              <Box display="flex" flexDirection="column" gap={1}>
                <ScoreBar label="Skill Score" score={skillScore} />
                <ScoreBar label="Behavior" score={behaviorScore} />
              </Box>
            </Paper>
        </Tooltip>
      )}
    </Draggable>
  );
}